import { useState } from 'react';
import { useCustom } from '@refinedev/core';
import type { BaseRecord } from '@refinedev/core';
import type { PageMeta } from '@/api/resources';
import { daysAgo, toDateInput } from '@/lib/format';

/**
 * The shared date window for the analytics screens.
 *
 * ⚠️ Every number comes from the nightly `link_click_daily` rollup, so today is
 * absent until it runs. The default window therefore ends yesterday — a range
 * ending today would draw a cliff to zero every morning and be read as a
 * collapse in traffic rather than a rollup that has not run.
 */
export interface DateRange {
  from: string;
  to: string;
}

export function useDateRange(days = 30): [DateRange, (next: DateRange) => void] {
  const [range, setRange] = useState<DateRange>(() => ({
    from: daysAgo(days),
    to: daysAgo(1),
  }));
  return [range, setRange];
}

export const YESTERDAY = daysAgo(1);
export const TODAY = toDateInput(new Date());

export interface AnalyticsPage<T> {
  rows: T[] | undefined;
  meta: PageMeta | undefined;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
}

/**
 * A page of a cursor-paginated analytics endpoint.
 *
 * The response is `{ data, meta }` and `meta.nextCursor` is opaque — it is
 * passed straight back and never parsed (CLAUDE.md §5). There is no total and
 * no page number anywhere in this file, because the API exposes neither and a
 * "page 3 of 12" would be invented.
 */
/**
 * The largest page the analytics endpoints accept.
 *
 * ⚠️ Was 100 at two call sites, and the API caps it at 50 — so every analytics
 * screen answered `400 VALIDATION_ERROR` on first load and rendered an error
 * where a chart belonged. Declared once here so a third caller cannot pick a
 * number the server refuses.
 */
export const ANALYTICS_PAGE_LIMIT = 50;

export function useAnalyticsPage<T>(
  path: string,
  query: Record<string, string | number | undefined>,
  cursor: string | null,
): AnalyticsPage<T> {
  const result = useCustom<BaseRecord>({
    url: path,
    method: 'get',
    config: { query: { ...query, ...(cursor ? { cursor } : {}) } },
    queryOptions: { retry: 0 },
  });

  const body = result.query.isSuccess
    ? (result.result.data as unknown as { data: T[]; meta: PageMeta })
    : undefined;

  return {
    rows: body?.data,
    meta: body?.meta,
    isLoading: result.query.isPending,
    error: result.query.error,
    refetch: () => {
      void result.query.refetch();
    },
  };
}
